import { getLoggerManager } from '@/shared/logging'
import type {
  FrameReadbackResult,
  FrameReaderLike,
  FrameReaderOptions,
  FrameReaderSource,
  ManualFrameRequest,
  VideoRenderReadbackDiagnostics,
} from './types'

const DEFAULT_MAX_PIXEL_COUNT = 3840 * 2160
const DEFAULT_MAX_PENDING_READBACKS = 1
const log = getLoggerManager().getLogger('videoRender:readback')

export class FrameReader implements FrameReaderLike {
  #pendingReadbacks = 0

  constructor(
    private readonly source: FrameReaderSource,
    private readonly options: FrameReaderOptions = {}
  ) {}

  async readFrame(request: ManualFrameRequest): Promise<FrameReadbackResult> {
    this.#validateRequest(request)

    const maxPendingReadbacks = this.options.maxPendingReadbacks ?? DEFAULT_MAX_PENDING_READBACKS
    if (this.#pendingReadbacks >= maxPendingReadbacks) {
      log.warn(
        `[FrameReader] readback backpressure, pending=${this.#pendingReadbacks}, maxPending=${maxPendingReadbacks}`
      )
      throw createReadbackError(
        'VIDEO_RENDER_READBACK_BACKPRESSURE',
        'Frame readback backpressure limit reached.'
      )
    }

    const pixelCount = request.width * request.height
    const maxPixelCount = this.options.maxPixelCount ?? DEFAULT_MAX_PIXEL_COUNT
    if (pixelCount > maxPixelCount) {
      log.warn(
        `[FrameReader] readback rejected, reason=too-large, width=${request.width}, height=${request.height}, pixels=${pixelCount}, maxPixels=${maxPixelCount}`
      )
      throw createReadbackError(
        'VIDEO_RENDER_READBACK_TOO_LARGE',
        'Frame readback pixel count exceeds configured limit.'
      )
    }

    const readWidth = Math.max(1, Math.round(request.width * request.pixelRatio))
    const readHeight = Math.max(1, Math.round(request.height * request.pixelRatio))
    const readPixelCount = readWidth * readHeight
    if (readPixelCount > maxPixelCount) {
      log.warn(
        `[FrameReader] readback rejected, reason=too-large-after-ratio, width=${readWidth}, height=${readHeight}, pixels=${readPixelCount}, maxPixels=${maxPixelCount}`
      )
      throw createReadbackError(
        'VIDEO_RENDER_READBACK_TOO_LARGE',
        'Frame readback pixel count exceeds configured limit.'
      )
    }

    const readBuffer = new Uint8Array(readPixelCount * 4)
    const startMs = nowMs()
    this.#pendingReadbacks += 1
    try {
      const read = await this.#readRgba(request, readWidth, readHeight, readBuffer)
      if (read.origin === 'bottom-left') {
        flipRgbaRowsInPlace(readBuffer, readWidth, readHeight)
      }
      const buffer =
        readWidth === request.width && readHeight === request.height
          ? readBuffer
          : resizeRgbaNearest(readBuffer, readWidth, readHeight, request.width, request.height)
      const durationMs = nowMs() - startMs
      const diagnostics: VideoRenderReadbackDiagnostics = {
        strategy: read.strategy,
        durationMs,
        byteLength: buffer.byteLength,
        maxPixelCount,
        pendingReadbacks: this.#pendingReadbacks - 1,
        backpressure:
          durationMs > (this.options.warnIfDurationExceedsMs ?? Number.POSITIVE_INFINITY),
        origin: 'top-left',
        colorSpace: this.options.colorSpace ?? 'srgb',
        alphaMode: this.options.alphaMode ?? 'straight',
        yFlipped: read.origin === 'bottom-left',
      }
      if (diagnostics.backpressure) {
        log.warn(
          `[FrameReader] slow readback, frame=${request.frameNumber}, strategy=${read.strategy}, width=${readWidth}, height=${readHeight}, duration=${durationMs.toFixed(1)}ms`
        )
      }
      return {
        width: request.width,
        height: request.height,
        pixelRatio: request.pixelRatio,
        frameNumber: request.frameNumber,
        timestampSeconds: request.timestampSeconds,
        data: buffer,
        colorSpace: diagnostics.colorSpace ?? 'srgb',
        alphaMode: diagnostics.alphaMode ?? 'straight',
        origin: 'top-left',
        yFlipped: read.origin === 'bottom-left',
        diagnostics,
      }
    } finally {
      this.#pendingReadbacks -= 1
    }
  }

  dispose(): void {
    void this.source.dispose?.()
  }

  async #readRgba(
    request: ManualFrameRequest,
    width: number,
    height: number,
    buffer: Uint8Array
  ): Promise<{
    strategy: VideoRenderReadbackDiagnostics['strategy']
    origin: 'top-left' | 'bottom-left'
  }> {
    if (this.source.readPixelsAsync) {
      const result = await this.source.readPixelsAsync(request, width, height)
      if (result.data.byteLength !== buffer.byteLength) {
        throw createReadbackError(
          'VIDEO_RENDER_READBACK_INVALID_SIZE',
          `Frame readback returned ${result.data.byteLength} bytes, expected ${buffer.byteLength}.`
        )
      }
      buffer.set(result.data)
      return { strategy: 'backend-capture', origin: result.origin ?? 'top-left' }
    }

    const renderer = this.source.renderer
    if (renderer?.readRenderTargetPixels && this.source.renderTarget) {
      await renderer.readRenderTargetPixels(this.source.renderTarget, 0, 0, width, height, buffer)
      return { strategy: 'render-target-readpixels', origin: 'bottom-left' }
    }

    if (!this.options.allowBackbufferRead || !this.source.preserveDrawingBuffer) {
      log.warn('[FrameReader] readback source unavailable, reason=render-target-required')
      throw createReadbackError(
        'VIDEO_RENDER_READBACK_SOURCE_UNAVAILABLE',
        'FrameReader requires a render target readPixels source or explicit preserveDrawingBuffer backbuffer read.'
      )
    }

    const gl = renderer?.getContext?.()
    if (!gl?.readPixels) {
      log.warn('[FrameReader] readback source unavailable, reason=readPixels-missing')
      throw createReadbackError(
        'VIDEO_RENDER_READBACK_SOURCE_UNAVAILABLE',
        'WebGL readPixels is unavailable.'
      )
    }
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, buffer)
    return { strategy: 'backbuffer-readpixels', origin: 'bottom-left' }
  }

  #validateRequest(request: ManualFrameRequest): void {
    if (request.width <= 0 || request.height <= 0 || request.pixelRatio <= 0) {
      log.warn(
        `[FrameReader] invalid readback size, width=${request.width}, height=${request.height}, pixelRatio=${request.pixelRatio}`
      )
      throw createReadbackError(
        'VIDEO_RENDER_READBACK_INVALID_SIZE',
        'Frame readback size must be positive.'
      )
    }
  }
}

export function createFrameReader(
  source: FrameReaderSource,
  options: FrameReaderOptions = {}
): FrameReader {
  return new FrameReader(source, options)
}

export function createMockFrameReader(): FrameReaderLike {
  return {
    async readFrame(request) {
      const byteLength = request.width * request.height * 4
      return {
        width: request.width,
        height: request.height,
        pixelRatio: request.pixelRatio,
        frameNumber: request.frameNumber,
        timestampSeconds: request.timestampSeconds,
        data: new Uint8Array(byteLength),
        colorSpace: 'srgb',
        alphaMode: 'straight',
        origin: 'top-left',
        yFlipped: true,
        diagnostics: {
          strategy: 'mock',
          byteLength,
          origin: 'top-left',
          colorSpace: 'srgb',
          alphaMode: 'straight',
          yFlipped: true,
        },
      }
    },
  }
}

export function flipRgbaRowsInPlace(data: Uint8Array, width: number, height: number): void {
  const rowLength = width * 4
  const scratch = new Uint8Array(rowLength)
  for (let top = 0, bottom = height - 1; top < bottom; top += 1, bottom -= 1) {
    const topOffset = top * rowLength
    const bottomOffset = bottom * rowLength
    scratch.set(data.subarray(topOffset, topOffset + rowLength))
    data.copyWithin(topOffset, bottomOffset, bottomOffset + rowLength)
    data.set(scratch, bottomOffset)
  }
}

export function resizeRgbaNearest(
  source: Uint8Array,
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  targetHeight: number
): Uint8Array {
  const target = new Uint8Array(targetWidth * targetHeight * 4)
  for (let y = 0; y < targetHeight; y += 1) {
    const sourceY = Math.min(sourceHeight - 1, Math.floor((y / targetHeight) * sourceHeight))
    for (let x = 0; x < targetWidth; x += 1) {
      const sourceX = Math.min(sourceWidth - 1, Math.floor((x / targetWidth) * sourceWidth))
      const sourceOffset = (sourceY * sourceWidth + sourceX) * 4
      const targetOffset = (y * targetWidth + x) * 4
      target[targetOffset] = source[sourceOffset]
      target[targetOffset + 1] = source[sourceOffset + 1]
      target[targetOffset + 2] = source[sourceOffset + 2]
      target[targetOffset + 3] = source[sourceOffset + 3]
    }
  }
  return target
}

function createReadbackError(code: string, message: string): Error {
  return Object.assign(new Error(message), { code })
}

function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}
